<template>
    <div class="layout">
        <NuxtLoadingIndicator color="#e6007e" />
        <Header
            v-if="!isWebviewInApp"
            :is-desktop="$device.isDesktop"
            :is-minimal-header="isMinimalHeader"
            :items="menuItems"
            :basket="basket"
            :cart-box-banner="cartBoxBanner"
            :pinned-categories="pinnedCategories"
            :menu-banners="menuBanners"
            :sale-banners="saleBanners"
            :is-user-authenticated="isUserAuthenticated"
            :username="username"
            :basic-url="basicUrl"
            :lang-default="locale"
            :favorite-count="favoriteCount"
            :is-pwa="true"
            :menu-item-products="isNotRo ? menuItemProducts : []"
            :is-chat-enabled="isChatEnabled"
            :xmas-category-id="xmasCategoryId"
            :headline-bar-data="headlineBar"
            :menu-highlight-category="menuHighlightCategory"
            :last-visited-product-ids="lastVisitedProductIds"
            :product-amounts="productAmounts"
            class="topHeader"
            :progress-bars="progressBars"
            :dictionary="headerDictionary"
            :is-xmas-season="isXmasSeason"
            @menu-is-visible="loadMenuFromApi"
            @mouseover="headerMouseOver"
            @add-product-to-basket="addProductToBasket"
            @redirect-to-url="redirectToUrl"
            @autocomplete-results="autocompleteResults"
            @autocomplete-click-result="autocompleteClickResult"
        />
        <div
            id="pageWrap"
            class="pageWrap"
            :class="{
                'pageWrap--with-xmas-banner': isXmasSeason && headlineBar,
                'pageWrap--with-headline-bar': headlineBar || isXmasSeason,
                'pageWrap--is-webview-in-app': isWebviewInApp,
            }"
        >
            <div class="d-flex m-auto w-100 justify-content-center container-page">
                <slot />
            </div>

            <Footer
                v-if="!isWebviewInApp"
                :lang="locale"
                :structured-data="structuredData"
                class="topFooter"
                :dictionary="$tm('footerDictionary')"
                :list-pay-methods="$tm('listPayMethods')"
                :is-pwa="true"
                @open-registration-pop-up="openRegistrationPopUp"
            />
        </div>
        <div id="overlay"></div>
        <Cookiebar :show-cookie="!isWebviewInApp" />
        <ModalDelivery />
        <MigrateToMobileAppModal />
        <ModalPriceInPharmacy />
        <DelayHydration>
            <ObjectsPreBasket />
        </DelayHydration>

        <!-- Preload CSS and JS for product and category page -->
        <div class="d-none">
            <nuxt-link to="/product-page" rel="nofollow"></nuxt-link>
            <nuxt-link to="/category-page" rel="nofollow"></nuxt-link>
        </div>

        <ElementsRegistartionModal v-if="isPopUpRegistrationInQuery || footerRegistrationModal" :footer-modal="footerRegistrationModal" />
    </div>
</template>

<script lang="ts">
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NuxtApp, NuxtSSRContext, useNuxtApp } from '#app'
import { MetaObject } from '@nuxt/schema'
import Header from '../node_modules/eshop-pwa-header/src/components/Header.vue'
import Footer from '../node_modules/eshop-pwa-footer/src/components/TheFooter.vue'
import DataLayer from '~/extensions/DataLayer'
import Cookiebar from '~/components/objects/Cookiebar.vue'
import { useLayoutStore } from '~/stores/layout'
import { addProductToBasket } from '~/utils/addProductToBasket'
import { useBasketStore } from '~/stores/basket'
import { useAuthStore } from '~/stores/auth'
import { usePreBasketStore } from '~/stores/preBasket'
import MigrateToMobileAppModal from '~/components/modal/MigrateToMobileAppModal.vue'
import { GtmItemListNameEnum } from '~/models/DataLayer/GtmItemListNameEnum'
import { GtmEcommerceEventTypeEnum } from '~/models/DataLayer/GtmEcommerceEventTypeEnum'
import GtmEcommerceEvent from '~/models/DataLayer/GtmEcommerceEvent'
import { transformFullProductToGtmProduct } from '~/utils/transformFullProductToGtmProduct'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import GtmProduct from '~/models/Product/GtmProduct'

export default defineNuxtComponent({
    components: {
        MigrateToMobileAppModal,
        Footer,
        Header,
        Cookiebar,
    },
    transition: 'page',

    data() {
        return {
            lastScrollTop: 0,
            menuFullyLoaded: false,
            footerRegistrationModal: false,
        }
    },

    mounted() {
        const preBasketStore = usePreBasketStore()

        let listPosition = -1
        let bundleProducts: GtmProduct[] = []
        let productIdReferrer: number | null = null
        // @ts-ignore
        window.addToBasket = async function (productId, amount, listName = '') {
            const result = await useNuxtApp().$repository.getProductDetailData(productId)
            let skipPushToDataLayer = false
            if (listName.includes('dynamic_bundle')) {
                skipPushToDataLayer = true
                listName = GtmItemListNameEnum.ProductDetailDynamicBundle
                bundleProducts.push(transformFullProductToGtmProduct(result.product))
                if (productIdReferrer === null) {
                    productIdReferrer = result.product.id
                }
                listPosition++
            } else if (listName === 'prebasket') {
                listPosition = 0
                productIdReferrer = preBasketStore.productIdReferrer
            } else if (listName === 'detail') {
                listName = GtmItemListNameEnum.ProductDetailRecommendationProducts
            } else {
                listPosition = 0
            }

            addProductToBasket({
                fullProduct: result.product,
                productId: result.product.id,
                amount,
                showPreBasket: false,
                itemListName: listName,
                listPosition,
                skipPushToDataLayer,
                productIdReferrer,
            })

            if (skipPushToDataLayer && listPosition > 0) {
                const ecommerceEvent = {
                    type: GtmEcommerceEventTypeEnum.AddToCart,
                    products: bundleProducts,
                    itemListName: 'dynamic_bundles',
                    productAmount: 1,
                    productIdReferrer,
                } as GtmEcommerceEvent
                DataLayer.pushEcommerceEventToDataLayer(ecommerceEvent)

                productIdReferrer = null
                bundleProducts = []
            }

            if (listPosition >= 1) {
                listPosition = -1
            }
        }

        if (this.$device.isAndroid) {
            document.body.classList.add('isAndroid')
        }
        if (this.$device.isIos) {
            document.body.classList.add('isIos')
        }
        if (this.$device.isDesktop) {
            document.body.classList.add('isDesktop')
        }
        if (this.$device.isTablet) {
            document.body.classList.add('isTablet')
        }
        if (this.$device.isMobile) {
            document.body.classList.add('isMobile')
        }

        try {
            const authStore = useAuthStore()
            this.$repository.saveCjEventId(useRoute().query, authStore.accessToken)
        } catch {}
    },

    computed: {
        headerDictionary() {
            return Object.assign(
                {},
                this.$tm('header'),
                this.$tm('basic'),
                this.$tm('footerDictionary.customerSupport'),
                this.$tm('productDetail')
            )
        },
        productAmounts() {
            const basketStore = useBasketStore()
            return basketStore.productAmounts
        },

        isWebviewInApp() {
            return this.$route.query.webview ?? false
        },

        isChatEnabled() {
            return this.$config.public.isChatEnabled
        },

        xmasCategoryId() {
            return parseInt(this.$config.public.xmasCategoryId)
        },

        menuHighlightCategory() {
            return this.$config.public.menuHighlightCategory || null
        },

        basicUrl() {
            return useRuntimeConfig().public.apiUrl
        },

        isUserAuthenticated() {
            return this.authUser !== null
        },

        authUser() {
            const basketStore = useBasketStore()

            return basketStore.authUser
        },

        username() {
            const basketStore = useBasketStore()

            if (!this.isUserAuthenticated || !basketStore.authUser) {
                return this.$t('siteHeader.login.title')
            }

            if (basketStore.authUser.firstName === '') {
                return basketStore.authUser.email
            } else {
                return basketStore.authUser.firstName
            }
        },

        menuItems() {
            const layoutStore = useLayoutStore()

            return layoutStore.menuItems
        },

        headlineBar() {
            const layoutStore = useLayoutStore()
            return layoutStore.headlineBar
        },

        menuItemProducts() {
            const layoutStore = useLayoutStore()

            return layoutStore.menuItemProducts
        },

        cartBoxBanner() {
            const layoutStore = useLayoutStore()

            return layoutStore.cartBoxBanner || {}
        },

        locale() {
            return useRuntimeConfig().public.locale || 'cz'
        },

        structuredData() {
            return {
                rating: 4.8,
            }
            // const layoutStore = useLayoutStore()
            //
            // return layoutStore.structuredData
        },

        basket() {
            const basketStore = useBasketStore()

            return basketStore.basket
        },

        pinnedCategories() {
            const layoutStore = useLayoutStore()

            return layoutStore.pinnedCategories || []
        },

        menuBanners() {
            const layoutStore = useLayoutStore()

            return layoutStore.menuBanners || []
        },

        progressBars() {
            const basketStore = useBasketStore()

            return basketStore.progressBars || []
        },

        saleBanners() {
            const layoutStore = useLayoutStore()

            return layoutStore.saleBanners || []
        },

        favoriteCount() {
            const basketStore = useBasketStore()

            return basketStore.favoriteCount || 0
        },

        freeDelivery() {
            return 0
        },

        lastVisitedProductIds() {
            const idProducts = useCookie('lastViewProductsIds').value
            if (idProducts && idProducts.length > 2) {
                return idProducts
            }

            return []
        },

        isNotRo() {
            return useI18n().locale.value !== 'ro'
        },

        isMinimalHeader() {
            return ['cart.step_2', 'cart.step_3'].includes(this.$route.matched[0]?.props.default.componentName)
        },

        isXmasSeason() {
            const date = new Date()
            const currentDate = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate()
            return currentDate < this.$config.public.xmasDelivery
        },

        isPopUpRegistrationInQuery(): boolean {
            return this.$config.public.popUpRegistration && Object.keys(this.$route.query).includes(useI18n().t('popUp.query'))
        },
    },

    methods: {
        openRegistrationPopUp() {
            if (!this.isPopUpRegistrationInQuery) {
                this.footerRegistrationModal = true
            }

            setTimeout(() => {
                useNuxtApp().$bus.$emit('showModal', this.isPopUpRegistrationInQuery ? 'registration-modal' : 'footer-registration-modal')
            }, 200)
        },

        autocompleteResults(queryString, items) {
            if (queryString.trim() !== '') {
                LuigisBoxEventApi.sendAutocompleteEvent(queryString, items)
            }
        },

        autocompleteClickResult(clickType, productId) {
            if (clickType === 'conversion') {
                LuigisBoxEventApi.sendClickEvent('buy', productId)
            } else {
                LuigisBoxEventApi.sendClickEvent('click', productId)
            }
        },

        addProductToBasket({ product, amount, showPreBasket, itemListName }) {
            addProductToBasket({
                product,
                productId: product.id,
                amount,
                showPreBasket,
                itemListName,
            })
        },

        async redirectToUrl(event) {
            const href = event.currentTarget.getAttribute('href')
            const external = href.startsWith('http')

            await navigateTo(href, { external })
        },

        headerMouseOver() {
            if (this.$device.isDesktop) {
                this.loadMenuFromApi()
            }
        },

        loadMenuFromApi() {
            setTimeout(async () => {
                if (!this.menuFullyLoaded) {
                    this.menuFullyLoaded = true
                    const response = await this.$repository.getPwaMenu()
                    const layoutStore = useLayoutStore()
                    await layoutStore.setMenuBanners(response.menuBanners)
                    await layoutStore.setMenuItems(response.menuItems)
                    await layoutStore.setMenuItemProducts(response.menuItemProducts)
                }
            }, 0)
        },
    },

    head(nuxtApp: NuxtApp): MetaObject {
        let config
        if (process.client) {
            config = nuxtApp.payload.config
        } else {
            config = (nuxtApp.ssrContext as NuxtSSRContext).config
        }
        const baseUrl = config.public.apiUrl + nuxtApp._route.fullPath
        const appId = config.public.appId
        const layoutStore = useLayoutStore()

        const script: { src: string; async?: true; type?: string }[] = []
        if (layoutStore.zoeEnabled && config.public.zoeScript && !nuxtApp._route.query.nozoe) {
            script.push({
                src: config.public.zoeScript,
                async: true,
            })
        }
        if (config.public.luigisboxScript && !nuxtApp._route.query.noluigisbox) {
            script.push({
                src: config.public.luigisboxScript,
                async: true,
            })
        }

        if (config.public.isChatBotEnabled) {
            script.push({
                type: 'text/javascript',
                src: '/nuxt-assets/chatbot.js',
            })
        }

        return {
            bodyAttrs: {
                'data-lang': config.public.bodyLang,
            },
            htmlAttrs: {
                lang: config.public.htmlLang,
            },
            meta: [
                {
                    hid: 'apple-itunes-app',
                    name: 'apple-itunes-app',
                    content: baseUrl,
                },
                {
                    hid: 'al:ios:app_name',
                    property: 'al:ios:app_name',
                    content: 'Pilulka',
                },
                {
                    hid: 'al:ios:app_store_id',
                    property: 'al:ios:app_store_id',
                    content: appId,
                },
                {
                    hid: 'al:ios:url',
                    property: 'al:ios:url',
                    content: baseUrl,
                },
            ],
            script,
        }
    },
})
</script>

<style lang="scss" scoped>
@media (max-width: 768px) {
    .layout {
        overflow-x: hidden;
    }
}

.pageWrap {
    font-weight: 400;
    padding-top: 113px;

    &.pageWrap--is-webview-in-app {
        padding-top: 0;
    }

    &--with-headline-bar {
        padding-top: 154px;
    }

    &--with-xmas-banner {
        padding-top: 188px;
    }
}

//body.isAndroid .pageWrap {
//    padding-top: 143px;
//
//    &--with-headline-bar {
//        padding-top: 184px;
//    }
//
//    &--with-xmas-banner {
//        padding-top: 218px;
//    }
//}

.topFooter {
    content-visibility: auto;
}
</style>

<style lang="scss">
.site-footer-bottom {
    margin: 0 auto;
}
</style>
